import React, { useState } from 'react';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import './MainDeathCause.scss';

/**
 * 「2024年全港主要疾病死因」, an in-house replacement for the Piktochart of the same name, in the
 * look of ai.portfoplus.com: the Piktochart's pie as a donut, and a picker that lights up one cause
 * in a grid of 100 people who died of a disease. The figures are the Piktochart's own (2024, Centre
 * for Health Protection), kept as they are.
 */

type Cause = 'other' | 'cancer' | 'respiratory' | 'cardio';

/** Share of deaths from disease, per cent; the Piktochart's 2024 figures. */
export const SHARE: Record<Cause, number> = { other: 31.1, cancer: 26.2, respiratory: 24.5, cardio: 18.1 };
const CAUSES: Cause[] = ['other', 'cancer', 'respiratory', 'cardio'];
/** The three named causes, which the conclusion adds up. */
const NAMED: Cause[] = ['cancer', 'respiratory', 'cardio'];

/** Out of 100 people; the four shares round to 31 + 26 + 25 + 18 = 100. */
export const PEOPLE: Record<Cause, number> = { other: 31, cancer: 26, respiratory: 25, cardio: 18 };

const SOURCE: Record<string, string> = {
  en: 'https://www.chp.gov.hk/en/statistics/data/10/27/340.html',
  zh: 'https://www.chp.gov.hk/sc/statistics/data/10/27/340.html'
};
const SOURCE_TC = 'https://www.chp.gov.hk/tc/statistics/data/10/27/340.html';

const R = 80;
const RING = 34;
const CIRC = 2 * Math.PI * R;

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const MainDeathCause = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [picked, setPicked] = useState<Cause>('cancer');

  const total = CAUSES.reduce((sum, c) => sum + SHARE[c], 0);
  const named = Math.round(NAMED.reduce((sum, c) => sum + SHARE[c], 0) * 10) / 10;

  let offset = 0;
  const slices = CAUSES.map(cause => {
    const length = (SHARE[cause] / total) * CIRC;
    const slice = { cause, length, offset };
    offset += length;
    return slice;
  });

  let next = 0;
  const people = CAUSES.flatMap(cause => Array.from({ length: PEOPLE[cause] }, () => ({ cause, i: next++ })));

  return (
    <div className="main-death-cause" lang={HTML_LANG[intl.locale] || intl.locale}>
      <h2 className="mdc-title">
        <span>{t('mdc-title')}</span>
      </h2>

      <div className="mdc-body">
        <div className="mdc-donut">
          <svg viewBox="0 0 200 200" role="img" aria-label={t('mdc-title')}>
            <g transform="rotate(-90 100 100)">
              {slices.map(({ cause, length, offset: start }) => (
                <circle
                  key={cause}
                  className={`mdc-slice is-${cause}${cause === picked ? ' is-on' : ''}`}
                  cx="100"
                  cy="100"
                  r={R}
                  strokeWidth={RING}
                  strokeDasharray={`${length} ${CIRC - length}`}
                  strokeDashoffset={-start}
                  onClick={() => setPicked(cause)}
                />
              ))}
            </g>
          </svg>
          <div className="mdc-centre" aria-live="polite">
            <strong>{SHARE[picked]}%</strong>
            <span>{t(`mdc-${picked}`)}</span>
          </div>
        </div>

        <ul className="mdc-legend">
          {CAUSES.map(cause => (
            <li key={cause}>
              <button
                type="button"
                className={`is-${cause}${cause === picked ? ' is-on' : ''}`}
                aria-pressed={cause === picked}
                onClick={() => setPicked(cause)}
              >
                <i aria-hidden="true" />
                <span>{t(`mdc-${cause}`)}</span>
                <strong>{SHARE[cause]}%</strong>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <section className="mdc-people">
        <p aria-live="polite">{t('mdc-people', { n: PEOPLE[picked], cause: t(`mdc-${picked}`) })}</p>
        <div className="mdc-grid" aria-hidden="true">
          {people.map(({ cause, i }) => (
            <span key={i} className={`mdc-dot is-${cause}${cause === picked ? ' is-on' : ''}`} />
          ))}
        </div>
        <small>{t('mdc-people-note')}</small>
      </section>

      <div className="mdc-tip">
        <CampaignRoundedIcon aria-hidden="true" />
        <p>{t('mdc-conclusion', { pct: named })}</p>
      </div>

      <p className="mdc-source">
        {t('mdc-source')}
        <a href={SOURCE[intl.locale] || SOURCE_TC} target="_blank" rel="noopener noreferrer">
          {t('mdc-source-link')}
        </a>
      </p>
    </div>
  );
};

export default MainDeathCause;
